/**
 * AddEditProviderDialog Component
 * Modal for creating or editing training providers
 */

import { useState, useEffect } from 'react';
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Textarea } from '@/components/ui/textarea';
import { Switch } from '@/components/ui/switch';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select';
import { useCreateProvider, useManageProvider } from '@/hooks/useProviderManagement';
import { PROVIDER_TYPE_LABELS, type TrainingProviderExtended } from '@/types/training';
import { Loader2 } from 'lucide-react';
import { toast } from 'sonner';

interface AddEditProviderDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  provider?: TrainingProviderExtended | null; // If provided, edit mode
}

const STORAGE_KEY = 'add-provider-dialog-form';

interface StoredFormState {
  displayName: string;
  providerType: string;
  websiteUrl: string;
  description: string;
  logoUrl: string;
  discoveryEnabled: boolean;
  learningEnabled: boolean;
}

function getStoredFormState(): StoredFormState | null {
  try {
    const stored = sessionStorage.getItem(STORAGE_KEY);
    return stored ? JSON.parse(stored) : null;
  } catch {
    return null;
  }
}

function clearStoredFormState() {
  sessionStorage.removeItem(STORAGE_KEY);
}

export function AddEditProviderDialog({
  open,
  onOpenChange,
  provider,
}: AddEditProviderDialogProps) {
  const isEditMode = !!provider;
  
  // Initialize from sessionStorage for add mode only
  const storedState = !isEditMode ? getStoredFormState() : null;
  
  const [displayName, setDisplayName] = useState(storedState?.displayName ?? '');
  const [providerType, setProviderType] = useState(storedState?.providerType ?? 'educator');
  const [websiteUrl, setWebsiteUrl] = useState(storedState?.websiteUrl ?? '');
  const [description, setDescription] = useState(storedState?.description ?? '');
  const [logoUrl, setLogoUrl] = useState(storedState?.logoUrl ?? '');
  const [discoveryEnabled, setDiscoveryEnabled] = useState(storedState?.discoveryEnabled ?? true);
  const [learningEnabled, setLearningEnabled] = useState(storedState?.learningEnabled ?? true);

  const createProvider = useCreateProvider();
  const manageProvider = useManageProvider();

  // Persist form state to sessionStorage for add mode
  useEffect(() => {
    if (open && !isEditMode) {
      const formState: StoredFormState = {
        displayName,
        providerType,
        websiteUrl,
        description,
        logoUrl,
        discoveryEnabled,
        learningEnabled,
      };
      sessionStorage.setItem(STORAGE_KEY, JSON.stringify(formState));
    }
  }, [open, isEditMode, displayName, providerType, websiteUrl, description, logoUrl, discoveryEnabled, learningEnabled]);

  // Load provider data for edit mode
  useEffect(() => {
    if (open && provider) {
      setDisplayName(provider.display_name || '');
      setProviderType(provider.provider_type || 'educator');
      setWebsiteUrl(provider.website_url || '');
      setDescription(provider.description || '');
      setLogoUrl(provider.logo_url || '');
      setDiscoveryEnabled(provider.discovery_enabled ?? true);
      setLearningEnabled(provider.learning_enabled ?? true);
    }
  }, [open, provider]);

  const handleSubmit = async () => {
    if (!displayName.trim()) {
      toast.error('Display name is required');
      return;
    }

    try {
      if (isEditMode && provider) {
        await manageProvider.mutateAsync({
          action: 'update',
          providerId: provider.id,
          data: {
            display_name: displayName.trim(),
            provider_type: providerType,
            website_url: websiteUrl.trim() || null,
            description: description.trim() || null,
            logo_url: logoUrl.trim() || null,
          },
        });
        
        // Handle discovery/learning toggles separately if changed
        if (provider.discovery_enabled !== discoveryEnabled) {
          await manageProvider.mutateAsync({
            action: discoveryEnabled ? 'enable_discovery' : 'disable_discovery',
            providerId: provider.id,
          });
        }
        if (provider.learning_enabled !== learningEnabled) {
          await manageProvider.mutateAsync({
            action: learningEnabled ? 'resume_learning' : 'pause_learning',
            providerId: provider.id,
          });
        }
        
        toast.success('Provider updated');
      } else {
        await createProvider.mutateAsync({
          display_name: displayName.trim(),
          provider_type: providerType,
          website_url: websiteUrl.trim() || undefined,
          description: description.trim() || undefined,
          logo_url: logoUrl.trim() || undefined,
          discovery_enabled: discoveryEnabled,
          learning_enabled: learningEnabled,
        });
        toast.success('Provider created');
        clearStoredFormState();
      }
      onOpenChange(false);
    } catch (error: any) {
      toast.error(error.message || 'Failed to save provider');
    }
  };

  const isSubmitting = createProvider.isPending || manageProvider.isPending;

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-[500px]">
        <DialogHeader>
          <DialogTitle>
            {isEditMode ? 'Edit Provider' : 'Add Provider'}
          </DialogTitle>
          <DialogDescription>
            {isEditMode 
              ? 'Update provider information and settings.'
              : 'Add a new training content provider.'
            }
          </DialogDescription>
        </DialogHeader>

        <div className="grid gap-4 py-4">
          <div className="grid gap-2">
            <Label htmlFor="displayName">Display Name *</Label>
            <Input
              id="displayName"
              value={displayName}
              onChange={(e) => setDisplayName(e.target.value)}
              placeholder="e.g., Scanner Danner"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="providerType">Provider Type</Label>
            <Select value={providerType} onValueChange={setProviderType}>
              <SelectTrigger>
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                {Object.entries(PROVIDER_TYPE_LABELS).map(([value, label]) => (
                  <SelectItem key={value} value={value}>
                    {label}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>

          <div className="grid gap-2">
            <Label htmlFor="websiteUrl">Website URL</Label>
            <Input
              id="websiteUrl"
              type="url"
              value={websiteUrl}
              onChange={(e) => setWebsiteUrl(e.target.value)}
              placeholder="https://example.com"
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="description">Description</Label>
            <Textarea
              id="description"
              value={description}
              onChange={(e) => setDescription(e.target.value)}
              placeholder="Brief description of this provider..."
              rows={3}
            />
          </div>

          <div className="grid gap-2">
            <Label htmlFor="logoUrl">Logo URL</Label>
            <Input
              id="logoUrl"
              type="url"
              value={logoUrl}
              onChange={(e) => setLogoUrl(e.target.value)}
              placeholder="https://example.com/logo.png"
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <Label htmlFor="discoveryEnabled" className="cursor-pointer">
              Discovery Enabled
            </Label>
            <Switch
              id="discoveryEnabled"
              checked={discoveryEnabled}
              onCheckedChange={setDiscoveryEnabled}
            />
          </div>

          <div className="flex items-center justify-between p-3 bg-muted/30 rounded-lg">
            <Label htmlFor="learningEnabled" className="cursor-pointer">
              Learning Enabled
            </Label>
            <Switch
              id="learningEnabled"
              checked={learningEnabled}
              onCheckedChange={setLearningEnabled}
            />
          </div>
        </div>

        <DialogFooter>
          <Button variant="outline" onClick={() => onOpenChange(false)}>
            Cancel
          </Button>
          <Button onClick={handleSubmit} disabled={isSubmitting}>
            {isSubmitting && <Loader2 className="h-4 w-4 mr-2 animate-spin" />}
            {isEditMode ? 'Save Changes' : 'Create Provider'}
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default AddEditProviderDialog;
